import { faker } from '@faker-js/faker/locale/en';
import {
  StyleData,
  CustomField,
  Season,
  Brand,
  Group,
  StyleColor,
  ItemData,
  LinkedStyle,
  PurchaseOrderData,
  CreatedByUser,
  Organization,
  PoState,
} from 'features/purchase-orders/types';
import { itemStatuses } from 'mocks/api/handlers/purchaseOrdersMapping';

const seasons: Season[] = [
  { id: 1, name: 'Spring/Summer 2024' },
  { id: 2, name: 'Fall/Winter 2024' },
  { id: 3, name: 'Spring/Summer 2025' },
  { id: 4, name: 'Fall/Winter 2025' },
];

const brands: Brand[] = [
  { id: 1, name: 'Nike' },
  { id: 2, name: 'Adidas' },
  { id: 3, name: 'Puma' },
  { id: 4, name: 'Under Armour' },
  { id: 5, name: 'Reebok' },
];

const groups: Group[] = [
  { id: 1, name: 'Footwear' },
  { id: 2, name: 'Apparel' },
  { id: 3, name: 'Accessories' },
  { id: 4, name: 'Equipment' },
];

const styleColors: StyleColor[] = [
  {
    id: 1,
    colorId1: 'BLK',
    colorId2: null,
    name: 'Black',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 2,
    colorId1: 'WHT',
    colorId2: null,
    name: 'White',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 3,
    colorId1: 'RED',
    colorId2: null,
    name: 'Red',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 4,
    colorId1: 'BLU',
    colorId2: null,
    name: 'Blue',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 5,
    colorId1: 'GRN',
    colorId2: null,
    name: 'Green',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 6,
    colorId1: 'YEL',
    colorId2: null,
    name: 'Yellow',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 7,
    colorId1: 'GRY',
    colorId2: null,
    name: 'Gray',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
  {
    id: 8,
    colorId1: 'NAV',
    colorId2: null,
    name: 'Navy',
    hex: faker.color.rgb(),
    sizes: 'XL,L,M,S,XS',
  },
];

const customFields: CustomField[] = [
  { id: 1, name: 'Material Type', type: 0, position: 1, value: 'Cotton' },
  { id: 2, name: 'Production Date', type: 1, position: 2, value: '2024-03-15' },
  { id: 3, name: 'Description', type: 2, position: 3, value: 'Premium quality fabric' },
  { id: 4, name: 'Category', type: 3, position: 4, value: 'Sportswear' },
  { id: 5, name: 'Quantity', type: 4, position: 5, value: '1000' },
  { id: 6, name: 'Divider', type: 5, position: 6, value: '' },
  { id: 7, name: 'Supplier Code', type: 2, position: 7, value: 'SUP001' },
  { id: 8, name: 'Quality Rating', type: 4, position: 8, value: '9.5' },
];

export const style = (organizationId: number): StyleData => {
  const styleId = faker.number.int({ min: 1000, max: 9999 });
  const colorsCount = faker.number.int({ min: 1, max: 4 });
  const selectedColors = faker.helpers.arrayElements(styleColors, colorsCount);

  return {
    id: styleId,
    styleName: faker.commerce.productName(),
    styleNumber: `ST-${faker.number.int({ min: 1000, max: 9999 })}`,
    season: faker.helpers.arrayElement(seasons),
    brand: faker.helpers.arrayElement(brands),
    brandContactPerson: faker.person.fullName(),
    project: faker.helpers.maybe(() => faker.commerce.department(), { probability: 0.7 }),
    state: faker.number.int({ min: 0, max: 5 }),
    group: faker.helpers.arrayElement(groups),
    categories: faker.helpers
      .arrayElements(
        ['Sportswear', 'Casual', 'Formal', 'Outdoor'],
        faker.number.int({ min: 1, max: 3 })
      )
      .join(', '),
    description: faker.commerce.productDescription(),
    styleLogo: faker.helpers.maybe(() => faker.image.url(), { probability: 0.6 }),
    isPrimaryStyle: faker.datatype.boolean(0.3),
    styleColors: selectedColors,
    styleColorName: faker.helpers.maybe(
      () => faker.helpers.arrayElement(selectedColors)?.name || null,
      { probability: 0.5 }
    ),
    styleColorID: faker.helpers.maybe(
      () => faker.helpers.arrayElement(selectedColors)?.colorId1 || null,
      { probability: 0.5 }
    ),
    styleColorID2: null,
    customFields: faker.helpers.arrayElements(customFields, faker.number.int({ min: 2, max: 6 })),
    styleId,
    styleColorId: faker.helpers.maybe(
      () => faker.helpers.arrayElement(selectedColors)?.id || undefined,
      { probability: 0.5 }
    ),
    mappingStatus: faker.helpers.arrayElement(['include', 'exclude', 'partially-included']),
  };
};

export const styles = (organizationId: number, amount: number = 50): StyleData[] => {
  return Array.from({ length: amount }, () => style(organizationId));
};

export const styleCustomFields = (): CustomField[] => {
  return customFields.filter(field => field.type !== 5); // Exclude divider type
};

// Mock data for items
const itemSuppliers = ['Supplier A', 'Supplier B', 'Supplier C', 'Supplier D', 'Supplier E'];
const itemCategories = [
  'Raw Materials',
  'Components',
  'Finished Goods',
  'Packaging',
  'Accessories',
];
const weightUnits = ['kg', 'g', 'lb', 'oz'];

export const item = (organizationId: number): ItemData => {
  const itemId = faker.number.int({ min: 1000, max: 9999 });
  const linkedStylesCount = faker.number.int({ min: 0, max: 3 });
  const linkedStyles: LinkedStyle[] = Array.from({ length: linkedStylesCount }, () => ({
    id: faker.number.int({ min: 1000, max: 9999 }),
    styleName: faker.commerce.productName(),
    styleNumber: `ST-${faker.number.int({ min: 1000, max: 9999 })}`,
    season: faker.helpers.arrayElement(seasons),
    project: faker.helpers.maybe(() => faker.commerce.department(), { probability: 0.7 }),
    state: faker.number.int({ min: 0, max: 5 }),
    group: faker.helpers.arrayElement(groups),
  }));

  return {
    id: itemId,
    itemName: faker.commerce.productName(),
    itemNumber: `IT-${faker.number.int({ min: 1000, max: 9999 })}`,
    status: faker.helpers.arrayElement(itemStatuses),
    isMainItem: faker.datatype.boolean(0.3),
    itemComposition: faker.helpers.maybe(() => faker.commerce.productDescription(), {
      probability: 0.6,
    }),
    itemWeight: faker.helpers.maybe(
      () => faker.number.float({ min: 0.1, max: 10, multipleOf: 0.1 }).toString(),
      { probability: 0.8 }
    ),
    itemWeightUnit: faker.helpers.maybe(() => faker.helpers.arrayElement(weightUnits), {
      probability: 0.8,
    }),
    itemSupplier: faker.helpers.arrayElement(itemSuppliers),
    categories: faker.helpers.maybe(() => faker.helpers.arrayElement(itemCategories), {
      probability: 0.7,
    }),
    linkedStyles,
    itemLogo: faker.image.url(),
    mappingStatus: faker.helpers.arrayElement(['include', 'exclude', 'partially-included']),
  };
};

export const items = (organizationId: number, amount: number = 50): ItemData[] => {
  return Array.from({ length: amount }, () => item(organizationId));
};

// Mock data for purchase orders
const poStates: PoState[] = [
  { id: 1, name: 'Pending', localeCode: 'pending', uiCapabilities: ['edit', 'delete'] },
  {
    id: 2,
    name: 'Review & Approval',
    localeCode: 'review_approval',
    uiCapabilities: ['approve', 'reject'],
  },
  { id: 3, name: 'Done', localeCode: 'done', uiCapabilities: ['view'] },
  { id: 4, name: 'Cancelled', localeCode: 'cancelled', uiCapabilities: ['view'] },
];

const organizations: Organization[] = [
  { id: 1, name: 'Fashion Corp', type: 'company', status: 'active' },
  { id: 2, name: 'Style Inc', type: 'company', status: 'active' },
  { id: 3, name: 'Design Studio', type: 'company', status: 'active' },
  { id: 4, name: 'Creative Agency', type: 'company', status: 'active' },
];

const suppliers: Organization[] = [
  { id: 101, name: 'Global Textiles Ltd', type: 'supplier', status: 'active' },
  { id: 102, name: 'Premium Fabrics Co', type: 'supplier', status: 'active' },
  { id: 103, name: 'Elite Materials Inc', type: 'supplier', status: 'active' },
  { id: 104, name: 'Quality Suppliers Group', type: 'supplier', status: 'active' },
  { id: 105, name: 'International Trading Co', type: 'supplier', status: 'active' },
];

const users: CreatedByUser[] = [
  {
    id: '1',
    email: 'john.doe@company.com',
    name: 'John',
  },
  {
    id: '2',
    name: 'jane.smith',
    email: 'jane.smith@company.com',
  },
  {
    id: '3',
    name: 'mike.johnson',
    email: 'mike.johnson@company.com',
  },
  {
    id: '4',
    name: 'sarah.wilson',
    email: 'sarah.wilson@company.com',
  },
  {
    id: '5',
    name: 'david.brown',
    email: 'david.brown@company.com',
  },
];

export const purchaseOrder = (organizationId: number): PurchaseOrderData => {
  const poId = faker.number.int({ min: 1000, max: 9999 });

  return {
    id: `PO-${poId}`,
    organizationId,
    name: faker.commerce.productName(),
    supplierId: faker.helpers.arrayElement(suppliers).id,
    mappingSystemVersion: faker.number.int({ min: 1, max: 5 }),
    organization: faker.helpers.arrayElement(organizations),
    createdByUser: faker.helpers.arrayElement(users),
    supplier: faker.helpers.arrayElement(suppliers),
    status: faker.helpers.arrayElement(poStates),
    stylesCount: faker.number.int({ min: 1, max: 20 }),
    itemsCount: faker.number.int({ min: 5, max: 100 }),
  };
};

export const purchaseOrders = (
  organizationId: number,
  amount: number = 50
): PurchaseOrderData[] => {
  return Array.from({ length: amount }, () => purchaseOrder(organizationId));
};

// Helper function to generate sub-supplier details response
export const generateSubSupplierDetails = (supplierId: string) => {
  const processingTypesOptions = [
    'Cutting',
    'Sewing',
    'Dyeing',
    'Finishing',
    'Quality Control',
    'Packaging',
  ];
  const selectedProcessingTypes = faker.helpers.arrayElements(processingTypesOptions, {
    min: 2,
    max: 4,
  });

  const styleFieldData = Array.from(
    { length: faker.number.int({ min: 2, max: 5 }) },
    (_, index) => ({
      id: 1000 + index + 1,
      name: faker.commerce.productName(),
      userDefinedId: `SD-2024-${String(index + 1).padStart(3, '0')}`,
      processingTypes: faker.helpers
        .arrayElements(processingTypesOptions, { min: 1, max: 3 })
        .join(','),
    })
  );

  const itemFieldData = Array.from(
    { length: faker.number.int({ min: 2, max: 5 }) },
    (_, index) => ({
      id: 2000 + index + 1,
      name: faker.commerce.productName(),
      userDefinedId: `${faker.string.alpha(2).toUpperCase()}-${String(index + 1).padStart(3, '0')}`,
      processingTypes: faker.helpers
        .arrayElements(processingTypesOptions, { min: 1, max: 3 })
        .join(','),
    })
  );

  return {
    id: supplierId || `SUB-${String(faker.number.int({ min: 1, max: 999 })).padStart(3, '0')}`,
    name: faker.company.name(),
    avatarColor: faker.color.rgb(),
    country: faker.location.country(),
    address: faker.location.streetAddress(),
    zipCode: faker.location.zipCode(),
    city: faker.location.city(),
    stateOrRegion: faker.location.state(),
    latitude: faker.location.latitude(),
    longitude: faker.location.longitude(),
    processingTypes: selectedProcessingTypes.join(', '),
    styleFieldData,
    itemFieldData,
  };
};

// Mock data for supplier approvals
export const supplierApproval = (orgId: number) => {
  const facilityTypes = [
    'Auditor',
    'Casting',
    'Assembly',
    'Fulfillment Center Warehouse',
    'Production',
    'Quality Control',
    'Fabric',
    'Dyeing',
  ];
  const approvalStatuses = ['Awaiting Approval', 'Approved', 'Rejected', 'In Review'];
  const mappingStatuses = ['Not mapped', 'Mapping exists', 'Partial mapping'];

  const generateSubSupplierDetailsOptions = (shouldHaveSubsuppliers: boolean) => {
    if (!shouldHaveSubsuppliers) return [];

    return Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => ({
      id: faker.string.uuid().toUpperCase(),
      name: faker.company.name(),
      avatarColor: faker.color.rgb({ format: 'hex', casing: 'upper' }).replace('#', ''),
      parentOrganizationType: faker.number.int({ min: 0, max: 3 }),
    }));
  };

  // Generate 4-8 facility items
  const facilityCount = faker.number.int({ min: 4, max: 8 });

  return Array.from({ length: facilityCount }, (_unused, index) => {
    // type: 0 = organization, 1 = facility (based on your example)
    const type = faker.helpers.arrayElement([0, 1]);
    const shouldHaveSubsuppliers = type === 0; // Only organizations (type 0) have subsuppliers

    return {
      id: faker.number.int({ min: 1, max: 100 }),
      name: faker.helpers.arrayElement(facilityTypes),
      type,
      approvalStatus: faker.helpers.arrayElement(approvalStatuses),
      mappingStatus: faker.helpers.arrayElement(mappingStatuses),
      subsupplierDetails: generateSubSupplierDetailsOptions(shouldHaveSubsuppliers),
    };
  });
};
